import { Component, OnInit, AfterViewInit, HostListener, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FournisseurService } from '../../../../../../services/Fournisseur/fournisseur.service';
import { Fournisseur } from '../../../../../../models/fournisseur';
import { StationService } from '../../../../../../services/station.service';
import { DocumentService } from '../../../../../../services/document/document.service';
import { PaiementService } from '../../../../../../services/paiement/paiement.service';
import { environment } from '../../../../../../../environments/environment';
import * as Feather from 'feather-icons';
import flatpickr from 'flatpickr';
import { French } from 'flatpickr/dist/l10n/fr.js';

export interface ReleveTransaction {
  etape?: string;
  date: string;
  transaction: string;
  details: string;
  credit: string;
  debit: string;
  solde: string;
}

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const MONTHS_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

@Component({
  selector: 'app-fr-detail-contenu-releve-compte',
  templateUrl: './fr-detail-contenu-releve-compte.component.html',
  styleUrls: ['./fr-detail-contenu-releve-compte.component.scss']
})
export class FrDetailContenuReleveCompteComponent implements OnInit, AfterViewInit {

  fournisseur: Fournisseur | null = null;
  isLoading = false;

  @ViewChild('datePicker') datePicker!: ElementRef<HTMLInputElement>;

  viewMode: 'jours' | 'mois' | 'annees' = 'mois';
  showDatePicker = false;
  showViewDropdown = false;
  showYearDropdown = false;
  showPeriodPopup: 'jours' | 'mois' | 'annees' | null = null;
  weekDays: { label: string; dateString: string; isToday: boolean }[] = [];
  weekDayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  currentMonthIndex = new Date().getMonth();
  currentYear = new Date().getFullYear();
  selectedMonthIndex: number = new Date().getMonth();
  selectedDay = '';
  selectedYear = new Date().getFullYear();
  selectedDate: Date = new Date();
  selectedPeriod = '';

  monthsShort = MONTHS_SHORT;
  years: number[] = [];

  selectedMonth = new Date().getMonth() + 1;
  monthLabel = MONTHS_FR[new Date().getMonth()];
  dateDu = '';
  dateAu = '';
  periodShort = '';

  companyName = 'VShopi';
  companyAddress = 'Tunisie';
  companyPhones = '+1 (123) 456 7891, +44 (876) 543 2198';

  stationData: { nom_entreprise: string; logo: string } = { nom_entreprise: '', logo: '' };

  fournisseurName = '--';
  fournisseurAddress = '--';
  soldeInitial = 0;
  solde = 0;
  transactions: ReleveTransaction[] = [];
  paginatedTransactions: ReleveTransaction[] = [];
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  pages: number[] = [];
  readonly pageSizeOptions = [10, 25, 50, 100];

  private readonly STATUTS_AVOIR = [18, 19, 20];
  /** Tous les statuts à charger pour le relevé (Brouillon, Créé, Non payé, Payé, Partiellement payé, etc.) pour afficher toutes les factures. */
  private readonly STATUTS_RELEVE_FOURNISSEUR = [1, 3, 5, 6, 8, 9, 10, 11, 12, 13, 14, 15, 18, 19, 20];
  /** Id du type "FACTURE FOURNISSEUR" (récupéré via GET /api/statut-documents), fallback 14. */
  typeDocumentFactureFournisseurId: number | null = null;
  private readonly STATUT_PAYE_ID = 5;

  constructor(
    private route: ActivatedRoute,
    private fournisseurService: FournisseurService,
    private stationService: StationService,
    private documentService: DocumentService,
    private paiementService: PaiementService,
    private el: ElementRef
  ) {}

  ngOnInit(): void {
    const reference = this.route.parent?.snapshot.paramMap.get('reference');
    if (reference && reference !== 'add') {
      this.loadFournisseur(reference);
    } else {
      this.transactions = [];
      this.updatePagination();
    }
    this.years = Array.from({ length: 10 }, (_, i) => this.currentYear - 5 + i);
    this.generateWeekDays();
    this.updatePeriodFromSelection();
    this.loadStationData();
  }

  private getStationIdFromStorage(): number {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const station = currentUser?.stations?.[0];
      return station?.station_id ?? station?.id ?? currentUser?.station_id ?? 0;
    } catch {
      return 0;
    }
  }

  loadStationData(): void {
    const stationId = this.getStationIdFromStorage();
    if (!stationId) return;
    this.stationService.getStation(stationId).subscribe({
      next: (res) => {
        if (res?.success && res?.station) {
          const s = res.station;
          this.stationData.nom_entreprise = s.nom_entreprise || s.label || this.companyName;
          if (s.logo) {
            this.stationData.logo = s.logo.startsWith('http') ? s.logo : `${environment.url_images}/${s.logo}`.replace(/([^:]\/)\/+/g, '$1');
          }
          if (s.adresses?.[0]) {
            const a = s.adresses[0];
            this.companyAddress = [a.adresse, a.code_postal, a.pays].filter(Boolean).join(', ') || this.companyAddress;
          }
          if (s.telephones?.length) {
            this.companyPhones = s.telephones.map((t: any) => t.numero).filter(Boolean).join(', ') || this.companyPhones;
          }
        }
      },
      error: () => {}
    });
  }

  ngAfterViewInit(): void {
    Feather.replace();
    this.setCalendarIconWhite();
    setTimeout(() => this.initializeFlatpickr(), 100);
  }

  private initializeFlatpickr(): void {
    if (!this.datePicker?.nativeElement) return;
    const el = this.datePicker.nativeElement;
    const firstDay = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1);
    const lastDay = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth() + 1, 0);
    const fp = flatpickr(el, {
      locale: French,
      mode: 'range',
      dateFormat: 'Y-m-d',
      defaultDate: [firstDay, lastDay],
      allowInput: false,
      onChange: (selectedDates: Date[]) => {
        if (selectedDates.length === 1) {
          this.dateDu = this.formatDateFr(selectedDates[0]);
          this.dateAu = this.formatDateFr(selectedDates[0]);
          this.selectedDate = new Date(selectedDates[0]);
          this.selectedMonthIndex = this.selectedDate.getMonth();
          this.selectedYear = this.selectedDate.getFullYear();
          this.updatePeriodFromSelection();
        } else if (selectedDates.length === 2) {
          this.updatePeriodFromRange(selectedDates[0], selectedDates[1]);
        }
      },
      onClose: (selectedDates: Date[]) => {
        if (selectedDates.length === 1) {
          this.updatePeriodFromRange(selectedDates[0], selectedDates[0]);
        }
      }
    });
    (el as any)._flatpickr = fp;
  }

  openCalendarOnly(): void {
    this.showViewDropdown = false;
    this.showYearDropdown = false;
    const fp = (this.datePicker?.nativeElement as any)?._flatpickr;
    if (fp) {
      const start = this.parseDateFr(this.dateDu) || new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1);
      const end = this.parseDateFr(this.dateAu) || new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth() + 1, 0);
      fp.setDate([start, end], false);
      fp.open();
      setTimeout(() => {
        const calendar = document.querySelector('.flatpickr-calendar');
        if (calendar) {
          (calendar as HTMLElement).style.zIndex = '99999';
        }
      }, 50);
    }
    setTimeout(() => {
      Feather.replace();
      this.setCalendarIconWhite();
    }, 0);
  }

  private setCalendarIconWhite(): void {
    const btn = this.el.nativeElement.querySelector('.calendar-btn-blue');
    if (btn) {
      const svg = btn.querySelector('svg');
      if (svg) {
        svg.setAttribute('stroke', '#fff');
        svg.setAttribute('color', '#fff');
        svg.querySelectorAll('path, line, circle').forEach((el: SVGElement) => {
          el.setAttribute('stroke', '#fff');
        });
      }
    }
  }

  private loadFournisseur(reference: string): void {
    this.isLoading = true;
    const id = /^\d+$/.test(reference) ? parseInt(reference, 10) : reference;
    this.fournisseurService.getFournisseurById(id).subscribe({
      next: (data: Fournisseur) => {
        this.fournisseur = data;
        this.fournisseurName = data.nom_entreprise || '--';
        const addr = data.adresses?.[0];
        if (addr) {
          this.fournisseurAddress = [addr.adresse, addr.code_postal, addr.pays].filter(Boolean).join(', ') || '--';
        }
        const soldeAvance = (data as any).solde_avance;
        if (soldeAvance != null) {
          this.solde = typeof soldeAvance === 'string' ? parseFloat(soldeAvance) : Number(soldeAvance);
        }
        this.soldeInitial = 0;
        this.documentService.getTypeDocumentIdByTitre('FACTURE FOURNISSEUR').pipe(
          catchError(() => of(null))
        ).subscribe(id => {
          this.typeDocumentFactureFournisseurId = id ?? 14;
          this.loadReleveFromDocuments();
        });
        this.isLoading = false;
      },
      error: () => {
        this.transactions = [];
        this.updatePagination();
        this.isLoading = false;
      }
    });
  }

  loadReleveFromDocuments(): void {
    const fournisseurId = this.fournisseur?.id;
    if (fournisseurId == null) {
      this.transactions = [];
      this.updatePagination();
      return;
    }
    const dateFrom = this.parseDateFr(this.dateDu);
    const dateTo = this.parseDateFr(this.dateAu);
    if (!dateFrom || !dateTo) {
      this.transactions = [];
      this.updatePagination();
      return;
    }
    const tsFrom = dateFrom.getTime();
    const tsTo = dateTo.getTime();

    const filterByDateAndType = (list: any[]) =>
      list.filter(doc => {
        const docFournisseurId = doc.fournisseur_id ?? doc.fornisseur_id ?? doc.fournisseur?.id ?? doc.fornisseur_data?.fournisseur_id ?? doc.fournisseur_data?.id;
        const fournisseurIdFromDoc = docFournisseurId ?? (doc.fornisseurs && doc.fornisseurs[0] && doc.fornisseurs[0].fournisseur_id);
        if (fournisseurIdFromDoc != null && Number(fournisseurIdFromDoc) !== Number(fournisseurId)) return false;
        const typeId = doc.type_document_id ?? doc.typeDocument?.id;
        const typeIdNum = typeId != null ? Number(typeId) : null;
        const titre = (doc.typeDocument?.titre || doc.type_document?.titre || '').toUpperCase();
        const statutId = doc.statut_id ?? doc.statut?.id;
        const statutIdNum = statutId != null ? Number(statutId) : null;
        const isAvoir = (statutIdNum != null && this.STATUTS_AVOIR.includes(statutIdNum)) || titre.includes('AVOIR');
        const typeFactureFournId = this.typeDocumentFactureFournisseurId ?? 14;
        // Uniquement FACTURE FOURNISSEUR (type 14) ou avoir fournisseur
        const isFactureFourn = typeIdNum === typeFactureFournId || (titre || '').includes('FACTURE FOURNISSEUR');
        if (!isFactureFourn && !isAvoir) return false;
        const docDate = doc.date || doc.date_creation || doc.created_at;
        if (!docDate) return true;
        const t = new Date(docDate).getTime();
        return t >= tsFrom && t <= tsTo;
      });

    // POST /api/documents/by-fournisseur-statut : charger TOUS les statuts pour afficher factures payées, partiellement payées, non payées, avoirs
    const statutsToLoad = this.STATUTS_RELEVE_FOURNISSEUR;
    forkJoin(
      statutsToLoad.map(statutId =>
        this.documentService.getDocumentsByFournisseurStatutAll(fournisseurId, statutId, 100).pipe(catchError(() => of([])))
      )
    ).pipe(
      map((responses: any[][]) => {
        const byId = new Map<number, any>();
        responses.forEach(arr => (arr || []).forEach((doc: any) => {
          const id = doc?.id;
          if (id != null) byId.set(Number(id), doc);
        }));
        return filterByDateAndType(Array.from(byId.values()));
      }),
      switchMap((docs: any[]) => {
        if (docs.length === 0) {
          const emptyLines = this.buildReleveLinesFournisseur([], [], dateFrom, 0);
          return of({ lines: emptyLines });
        }
        const paiementCalls = docs.map(d => this.paiementService.getPaiementsByDocumentId(d.id).pipe(catchError(() => of([]))));
        return forkJoin(paiementCalls).pipe(
          map((paiementResponses: any[]) => ({ docs, paiementsByDoc: paiementResponses }))
        );
      })
    ).subscribe({
      next: (result: any) => {
        let lines: ReleveTransaction[] = [];
        if (result?.lines) {
          lines = result.lines;
        } else if (result?.docs != null && result?.paiementsByDoc != null) {
          lines = this.buildReleveLinesFournisseur(result.docs, result.paiementsByDoc, dateFrom!, 0);
        }
        this.transactions = lines;
        if (lines.length > 0) {
          const lastSolde = lines[lines.length - 1].solde;
          const match = lastSolde ? lastSolde.replace(/\s*TND$/, '').replace(/\s/g, '').replace(',', '.') : '';
          const n = parseFloat(match);
          if (!isNaN(n)) this.solde = n;
        }
        this.currentPage = 1;
        this.updatePagination();
      },
      error: () => {
        this.transactions = [];
        this.updatePagination();
      }
    });
  }

  updatePagination(): void {
    const total = this.transactions.length;
    this.totalPages = total > 0 ? Math.ceil(total / this.pageSize) : 1;
    this.currentPage = Math.min(Math.max(1, this.currentPage), this.totalPages);
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    const start = (this.currentPage - 1) * this.pageSize;
    this.paginatedTransactions = this.transactions.slice(start, start + this.pageSize);
  }

  changePage(page: number): void {
    this.currentPage = page;
    this.updatePagination();
  }

  get paginationStartIndex(): number {
    return this.transactions.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get paginationEndIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.transactions.length);
  }

  /**
   * Construit les lignes du relevé fournisseur : même logique que client (factures, avoirs, paiements)
   * mais avec débit/crédit inversés (compte fournisseur = ce qu’on doit au fournisseur).
   */
  private buildReleveLinesFournisseur(docs: any[], paiementsByDoc: any[], periodStart: Date, avanceFournisseur: number): ReleveTransaction[] {
    interface ReleveEvent { etape?: string; date: Date; transaction: string; details: string; credit: number; debit: number; infoOnly?: boolean; }
    const events: ReleveEvent[] = [];
    const ref = (d: any) => d?.ref_externe ?? d?.reference ?? (d ? `#${d.id}` : '');
    const dateEcheance = (d: any) => {
      if (!d) return '';
      const str = d.date_echeance ?? d.date_limite_paiement;
      if (!str) return '';
      const dd = new Date(str);
      return isNaN(dd.getTime()) ? '' : this.formatDateFr(dd);
    };
    const docById = new Map<number, any>(docs.map(d => [d.id, d]));
    const facturePaymentsShownInAvoir = new Set<number>();

    if (avanceFournisseur > 0) {
      events.push({ etape: '', date: periodStart, transaction: 'Avance', details: 'Avance fournisseur', credit: 0, debit: avanceFournisseur });
    }

    const sortedDocs = docs.slice().sort((a, b) => {
      const da = new Date(a.date || a.date_creation || a.created_at || 0).getTime();
      const db = new Date(b.date || b.date_creation || b.created_at || 0).getTime();
      if (da !== db) return da - db;
      const typeA = (a.type_document_id ?? a.typeDocument?.id) != null ? Number(a.type_document_id ?? a.typeDocument?.id) : 0;
      const typeB = (b.type_document_id ?? b.typeDocument?.id) != null ? Number(b.type_document_id ?? b.typeDocument?.id) : 0;
      const typeFactureFournId = this.typeDocumentFactureFournisseurId ?? 14;
      const isAvoirA = typeA !== typeFactureFournId || (a.statut_id != null && this.STATUTS_AVOIR.includes(Number(a.statut_id)));
      const isAvoirB = typeB !== typeFactureFournId || (b.statut_id != null && this.STATUTS_AVOIR.includes(Number(b.statut_id)));
      if (isAvoirA !== isAvoirB) return isAvoirA ? 1 : -1;
      return (a.document_original_id ?? 0) - (b.document_original_id ?? 0);
    });

    sortedDocs.forEach((doc, sortedIdx) => {
      const origIdx = docs.indexOf(doc);
      const idx = origIdx >= 0 ? origIdx : sortedIdx;
      const paiementResp = paiementsByDoc[idx] ?? [];
      const paiements = this.normalizePaiementsList(paiementResp);
      const netPayer = parseFloat(doc.net_payer ?? doc.total_ttc ?? '0') || 0;
      /** Montant total de la facture (pour afficher la ligne même si payée → net_payer = 0). */
      const montantFacture = parseFloat(doc.total_ttc ?? doc.net_payer ?? '0') || 0;
      const docTitre = (doc.typeDocument?.titre || doc.type_document?.titre || '').toUpperCase();
      const statutId = doc.statut_id ?? doc.statut?.id;
      const statutIdNum = statutId != null ? Number(statutId) : null;
      const isStatutAvoir = statutIdNum != null && this.STATUTS_AVOIR.includes(statutIdNum);
      const isAvoir = isStatutAvoir || (!(doc as any)._releveAsFacture && docTitre.includes('AVOIR'));
      const docDateStr = doc.date || doc.date_creation || doc.created_at;
      const docDate = docDateStr ? new Date(docDateStr) : periodStart;

      if (!isAvoir && montantFacture <= 0) return;

      if (isAvoir) {
        events.push({ etape: 'création avoir', date: docDate, transaction: 'Avoir', details: `${ref(doc)} dû le ${dateEcheance(doc) || this.formatDateFr(docDate)}`, credit: netPayer, debit: 0 });
        let factureLiee: any = null;
        let factureLieePaiements: any[] = [];
        for (let fi = 0; fi < docs.length; fi++) {
          const otherDoc = docs[fi];
          const otherPaiements = this.normalizePaiementsList(paiementsByDoc[fi] ?? []);
          const hasAvoirForThis = otherPaiements.some((p: any) => Number(p.avoir_id) === Number(doc.id));
          if (hasAvoirForThis) {
            factureLiee = otherDoc;
            factureLieePaiements = otherPaiements;
            facturePaymentsShownInAvoir.add(otherDoc.id);
            break;
          }
        }
        const factureLieeRef = factureLiee ? ref(factureLiee) : (doc.document_original_id != null ? ref(docById.get(Number(doc.document_original_id))) || `#${doc.document_original_id}` : '');
        factureLieePaiements.forEach((p: any) => {
          const dateP = new Date(p.date_paiement || p.created_at || p.date);
          if (Number(p.avoir_id) !== Number(doc.id)) return;
          (p.lignes_paiement || []).forEach((ligne: any) => {
            const typeP = (ligne.type_paiement || p.type_paiement || '').toString().toLowerCase();
            if (typeP !== 'avoir') return;
            const mt = parseFloat(ligne.montant || '0') || 0;
            if (mt <= 0) return;
            events.push({ etape: "utilisation avoir", date: dateP, transaction: "Utilisation d'un avoir", details: `Utilisation d'un avoir ${factureLieeRef}`, credit: 0, debit: mt });
            events.push({ etape: "utilisation avoir", date: dateP, transaction: 'Paiement de facture par avoir', details: `Compensation facture ${factureLieeRef}`, credit: mt, debit: 0 });
          });
        });
        factureLieePaiements.forEach((p: any) => {
          const dateP = new Date(p.date_paiement || p.created_at || p.date);
          if (p.retenue_source && p.Montant_RS != null && parseFloat(String(p.Montant_RS)) > 0) {
            const mtRs = parseFloat(String(p.Montant_RS)) || 0;
            const tauxRs = p.Taux_RS != null ? ` (${p.Taux_RS}%)` : '';
            events.push({ etape: 'paiement', date: dateP, transaction: 'Retenu à la source', details: `Retenue à la source${tauxRs} pour le paiement de ${factureLieeRef}`, credit: mtRs, debit: 0 });
          }
        });
        return;
      }

      events.push({ etape: 'validation facture', date: docDate, transaction: 'Facture fournisseur', details: `${ref(doc)} dû le ${dateEcheance(doc) || this.formatDateFr(docDate)}`, credit: 0, debit: montantFacture });
      if (facturePaymentsShownInAvoir.has(doc.id)) return;
      paiements.forEach((p: any) => {
        const dateP = new Date(p.date_paiement || p.created_at || p.date);
        if (p.retenue_source && p.Montant_RS != null && parseFloat(String(p.Montant_RS)) > 0) {
          const mtRs = parseFloat(String(p.Montant_RS)) || 0;
          const tauxRs = p.Taux_RS != null ? ` (${p.Taux_RS}%)` : '';
          events.push({ etape: 'paiement', date: dateP, transaction: 'Retenu à la source', details: `Retenu à la source${tauxRs} pour le paiement de ${ref(doc)}`, credit: mtRs, debit: 0 });
        }
        (p.lignes_paiement || []).forEach((ligne: any) => {
          const typeP = (ligne.type_paiement || p.type_paiement || 'espece').toString().toLowerCase();
          if (typeP === 'avoir') return;
          const mt = parseFloat(ligne.montant || '0') || 0;
          if (mt <= 0) return;
          const labelP = typeP === 'espece' ? 'Espèce' : typeP === 'cheque' ? 'Chèque' : typeP === 'virement' ? 'Virement' : typeP === 'traite' ? 'Traite' : typeP;
          events.push({ etape: 'paiement', date: dateP, transaction: labelP, details: `${labelP} pour le paiement de ${ref(doc)}`, credit: mt, debit: 0 });
        });
      });
    });

    const lines: ReleveTransaction[] = [];
    let runningSolde = this.soldeInitial;
    const zeroTnd = this.formatAmount(0) + ' TND';
    lines.push({
      etape: '',
      date: this.formatDateFr(periodStart),
      transaction: 'Solde initial',
      details: '',
      credit: zeroTnd,
      debit: zeroTnd,
      solde: this.formatAmount(runningSolde) + ' TND'
    });
    events.forEach(ev => {
      if (!ev.infoOnly) runningSolde += ev.debit - ev.credit;
      lines.push({
        etape: ev.etape ?? '',
        date: this.formatDateFr(ev.date),
        transaction: ev.transaction,
        details: ev.details,
        credit: ev.debit > 0 ? this.formatAmount(ev.debit) + ' TND' : zeroTnd,
        debit: ev.credit > 0 ? this.formatAmount(ev.credit) + ' TND' : zeroTnd,
        solde: this.formatAmount(runningSolde) + ' TND'
      });
    });
    return lines;
  }

  private normalizePaiementsList(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      if (Array.isArray(response.data)) return response.data;
      if (Array.isArray(response.data.paiements)) return response.data.paiements;
      if (response.data.paiement && typeof response.data.paiement === 'object') return [response.data.paiement];
    }
    if (response?.id) return [response];
    return [];
  }

  private dateToApi(ddmmyyyy: string): string {
    const d = this.parseDateFr(ddmmyyyy);
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private formatDateOperation(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return this.formatDateFr(d);
  }

  private formatDateFr(d: Date): string {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private parseDateFr(str: string): Date | null {
    if (!str || str.length < 10) return null;
    const [dd, mm, yyyy] = str.split('/').map(Number);
    if (!dd || !mm || !yyyy) return null;
    return new Date(yyyy, mm - 1, dd);
  }

  private updatePeriodFromRange(start: Date, end: Date): void {
    this.dateDu = this.formatDateFr(start);
    this.dateAu = this.formatDateFr(end);
    this.selectedDate = new Date(start);
    this.selectedMonthIndex = start.getMonth();
    this.selectedYear = start.getFullYear();
    this.selectedMonth = start.getMonth() + 1;
    this.monthLabel = MONTHS_FR[start.getMonth()];
    this.periodShort = `${String(start.getDate()).padStart(2, '0')}/${String(this.selectedMonth).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}/${String(end.getMonth() + 1).padStart(2, '0')}`;
    this.selectedPeriod = `Mois : ${this.monthLabel} ${this.selectedYear} ${this.periodShort}`;
    if (this.fournisseur?.id) this.loadReleveFromDocuments();
  }

  private updatePeriodFromSelection(): void {
    const d = this.selectedDate;
    this.selectedMonth = d.getMonth() + 1;
    this.selectedYear = d.getFullYear();
    this.selectedMonthIndex = d.getMonth();
    this.monthLabel = MONTHS_FR[d.getMonth()];
    let firstDay: Date;
    let lastDay: Date;
    if (this.viewMode === 'annees') {
      firstDay = new Date(this.selectedYear, 0, 1);
      lastDay = new Date(this.selectedYear, 11, 31);
    } else {
      firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
      lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    }
    this.dateDu = this.formatDateFr(firstDay);
    this.dateAu = this.formatDateFr(lastDay);
    this.periodShort = `${String(firstDay.getDate()).padStart(2, '0')}/${String(firstDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}/${String(lastDay.getMonth() + 1).padStart(2, '0')}`;
    if (this.viewMode === 'mois') {
      this.selectedPeriod = `Mois : ${this.monthLabel} ${this.selectedYear} ${this.periodShort}`;
    } else if (this.viewMode === 'annees') {
      this.selectedPeriod = `Année : ${this.selectedYear}`;
    } else {
      this.selectedPeriod = `Mois : ${this.monthLabel} ${this.selectedYear} ${this.periodShort}`;
    }
    if (this.fournisseur?.id) this.loadReleveFromDocuments();
  }

  get viewModeLabel(): string {
    if (this.viewMode === 'jours') return 'Jours';
    if (this.viewMode === 'annees') return 'Année';
    return 'Mois';
  }

  generateWeekDays(): void {
    const start = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1);
    const startOfWeek = new Date(start);
    startOfWeek.setDate(start.getDate() - start.getDay() + 1);
    this.weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      const day = d.getDate();
      const month = d.getMonth();
      const year = d.getFullYear();
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const today = new Date();
      const isToday = d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
      const label = `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}`;
      return { label, dateString, isToday };
    });
  }

  get weekDaysForPopup(): { dayName: string; label: string; dateString: string; isToday: boolean }[] {
    const d = new Date(this.selectedDate);
    const dayOfWeek = d.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startOfWeek = new Date(d);
    startOfWeek.setDate(d.getDate() + mondayOffset);
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
      const label = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
      return { dayName: this.weekDayNames[i], label, dateString, isToday };
    });
  }

  openPeriodPopup(mode: 'jours' | 'mois' | 'annees'): void {
    this.showViewDropdown = false;
    this.showYearDropdown = false;
    if (this.showPeriodPopup === mode) {
      this.showPeriodPopup = null;
    } else {
      this.showPeriodPopup = mode;
      if (mode === 'jours') {
        this.generateWeekDays();
        const today = new Date();
        this.selectedDate = new Date(today);
        this.updatePeriodFromRange(today, today);
      } else if (mode === 'annees') {
        this.updateYearsAroundSelected();
      }
    }
    setTimeout(() => Feather.replace(), 0);
  }

  private updateYearsAroundSelected(): void {
    this.years = Array.from({ length: 10 }, (_, i) => this.selectedYear - 5 + i);
  }

  get selectedDayDateString(): string {
    const d = this.parseDateFr(this.dateDu);
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  closePeriodPopup(): void {
    this.showPeriodPopup = null;
    setTimeout(() => Feather.replace(), 0);
  }

  selectDayInPopup(day: { dateString: string }): void {
    this.selectedDay = day.dateString;
    const [y, m, d] = day.dateString.split('-').map(Number);
    this.selectedDate = new Date(y, m - 1, d);
    this.updatePeriodFromRange(this.selectedDate, this.selectedDate);
    const fp = (this.datePicker?.nativeElement as any)?._flatpickr;
    if (fp) {
      fp.setDate([this.selectedDate, this.selectedDate], false);
    }
    this.closePeriodPopup();
  }

  selectMonthInPopup(index: number): void {
    this.viewMode = 'mois';
    this.selectMonthByIndex(index);
    this.closePeriodPopup();
  }

  changeYearInMonthPopup(delta: number): void {
    this.selectedYear += delta;
    this.selectedDate = new Date(this.selectedYear, this.selectedMonthIndex, 1);
    this.updatePeriodFromSelection();
  }

  selectYearInPopup(y: number): void {
    this.viewMode = 'annees';
    this.selectYear(y);
    this.closePeriodPopup();
  }

  decrementDate(): void {
    if (this.viewMode === 'mois') {
      this.selectedMonthIndex--;
      if (this.selectedMonthIndex < 0) {
        this.selectedMonthIndex = 11;
        this.selectedYear--;
      }
      this.selectedDate = new Date(this.selectedYear, this.selectedMonthIndex, 1);
    } else if (this.viewMode === 'annees') {
      this.selectedYear--;
      this.selectedDate = new Date(this.selectedYear, 0, 1);
    } else {
      this.selectedDate.setDate(this.selectedDate.getDate() - 1);
    }
    this.generateWeekDays();
    this.updatePeriodFromSelection();
  }

  incrementDate(): void {
    if (this.viewMode === 'mois') {
      this.selectedMonthIndex++;
      if (this.selectedMonthIndex > 11) {
        this.selectedMonthIndex = 0;
        this.selectedYear++;
      }
      this.selectedDate = new Date(this.selectedYear, this.selectedMonthIndex, 1);
    } else if (this.viewMode === 'annees') {
      this.selectedYear++;
      this.selectedDate = new Date(this.selectedYear, 0, 1);
    } else {
      this.selectedDate.setDate(this.selectedDate.getDate() + 1);
    }
    this.generateWeekDays();
    this.updatePeriodFromSelection();
  }

  changeViewMode(mode: 'jours' | 'mois' | 'annees'): void {
    this.viewMode = mode;
    if (mode === 'mois') {
      this.selectedMonthIndex = this.selectedDate.getMonth();
      this.selectedYear = this.selectedDate.getFullYear();
    } else if (mode === 'annees') {
      this.selectedYear = this.selectedDate.getFullYear();
    } else {
      this.generateWeekDays();
    }
    this.updatePeriodFromSelection();
    setTimeout(() => Feather.replace(), 0);
  }

  selectMonthByIndex(index: number): void {
    this.selectedMonthIndex = index;
    this.selectedYear = this.selectedDate.getFullYear();
    this.selectedDate = new Date(this.selectedYear, index, 1);
    this.updatePeriodFromSelection();
    setTimeout(() => Feather.replace(), 0);
  }

  selectYear(y: number): void {
    this.selectedYear = y;
    this.selectedDate = new Date(y, this.selectedDate.getMonth(), 1);
    this.selectedMonthIndex = this.selectedDate.getMonth();
    this.updatePeriodFromSelection();
    setTimeout(() => Feather.replace(), 0);
  }

  selectDay(day: { dateString: string }): void {
    this.selectedDay = day.dateString;
    const [y, m, d] = day.dateString.split('-').map(Number);
    this.selectedDate = new Date(y, m - 1, d);
    this.updatePeriodFromSelection();
  }

  toggleDatePicker(): void {
    this.openCalendarOnly();
  }

  toggleViewDropdown(): void {
    this.showViewDropdown = !this.showViewDropdown;
    this.showDatePicker = false;
    this.showYearDropdown = false;
    setTimeout(() => Feather.replace(), 0);
  }

  toggleYearDropdown(): void {
    this.showYearDropdown = !this.showYearDropdown;
    this.showDatePicker = false;
    this.showViewDropdown = false;
    setTimeout(() => Feather.replace(), 0);
  }

  changeYear(delta: number): void {
    this.selectedYear += delta;
    this.selectedDate = new Date(this.selectedYear, this.selectedMonthIndex, 1);
    this.updatePeriodFromSelection();
  }

  get soldeInitialFormatted(): string {
    return this.formatAmount(this.soldeInitial);
  }

  get soldeFormatted(): string {
    return this.formatAmount(this.solde);
  }

  private formatAmount(value: number): string {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
      useGrouping: true
    }).format(value).replace(/\u00A0/g, ' ');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.period-popup') || target.closest('.date-picker-popup') || target.closest('.dropdown-menu')) return;
    if (this.showDatePicker && target.classList.contains('date-picker-overlay')) {
      this.showDatePicker = false;
    }
    if (!target.closest('.dropdown-wrapper')) {
      this.showViewDropdown = false;
      this.showYearDropdown = false;
    }
    if (this.showPeriodPopup && !target.closest('.period-popup-wrapper') && !target.closest('.btn-period')) {
      this.closePeriodPopup();
    }
    this.closeFlatpickrIfClickOutside(target);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    this.closeFlatpickrIfClickOutside(target);
  }

  private closeFlatpickrIfClickOutside(target: HTMLElement): void {
    if (target.closest('.flatpickr-calendar') || target.closest('.calendar-wrapper')) return;
    const fp = this.getFlatpickrInstance();
    if (fp) {
      fp.close();
    }
  }

  private getFlatpickrInstance(): any {
    const input = this.datePicker?.nativeElement ?? this.el.nativeElement.querySelector('.flatpickr-input-releve');
    return input ? (input as any)._flatpickr : null;
  }
}
